import { activeProfileRequest } from 'data/requests/activeProfile';
import { logoutRequest } from 'data/requests/logout';
import { split } from 'lodash';
import LoadingContent from 'lqa-ui/lib/Components/LoadingContent';
import FrameAdmin from 'lqa-ui/lib/Frames/Admin';
import { IWithOverlaysProps, withOverlays } from 'lqa-ui/lib/Hocs/Overlay';
import MenuMobile from 'lqa-ui/lib/Components/MenuMobile';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { AnyAction, compose, Dispatch } from 'redux';
import { mutateAsync, updateEntities } from 'redux-query';
import { connectRequest } from 'redux-query-react';
import ErrorOffline from 'screens/errors/offline/Offline';
import ScreenLogin from 'screens/login/Login';
import { generateLocaledUrl } from 'helpers/generateLocaledUrl';
import { getMenuItems } from 'helpers/getMenuItems';
import { getItem, removeItem, setItem } from 'helpers/storage';
import { IProfile } from '../../interfaces/Profile';
import { IRouteParams } from '../../routers/LocaleRouter';
import { IUser } from '../../interfaces/user/user';
import { IUserhubProps } from 'lqa-ui/lib/Frames/Admin/Userhub';
import { ScrollLockingOverlay } from 'components/scrollLockingOverlay/ScrollLockingOverlay';
import { getUserData } from 'helpers/user/getUserData';
import { getUserAccountType } from 'helpers/user/getUserAccountType';
import { isUserManagementAllowed, isUserDepartmentManager, isUserViewer } from 'helpers/user/isUserManagementAllowed';
import { isUserBlackoutEditEnabled } from 'helpers/user/isUserBlackoutEditEnabled';
import { isOnAdminSide } from 'helpers/user/isOnAdminSide';
import EmailVerification from 'screens/emailVerification/EmailVerification';
import { getHeaders } from 'helpers/request/getHeaders';
import { TutorialVideoButton } from './TutorialVideoButton';
import LoginSSO from 'screens/login/LoginSSO';
import LoginSAML from 'screens/login/LoginSAML';
import { isMylqaSubscriptionActive } from 'helpers/hotel/isMylqaSubscriptionActive';
import Modal from 'components/overlay/Modal';
import { LqaAcademyNews } from './LqaAcademyNews';
import { shouldShowLqaAcademyModal } from 'helpers/LqaAcademyNewsPopUpDisplay';
 
interface IProps extends IWithOverlaysProps, RouteComponentProps<IRouteParams>, WithTranslation {
    children?: any;
    title: string;
    lang: string;
    activeProfile: IProfile | null;
    originalUser: IProfile | null;
    sessionProfile: IUser | null;
    logout: () => Promise<any>;
    restoreActiveProfile: (profile: IProfile) => void;
    profileQuery?: {
        url: string,
        isFinished: boolean,
        isPending: boolean,
    };
    hotelBrandSelection: {
        brandId: number,
        hotelId: number,
    };
}
 
interface IState {
    requiredStepsShown: boolean;
    brandId: number;
    hotelId: number;
    isLqaAcademyModalOpen: boolean;
}
 
const queryKey = 'activeProfile';
 
const mapStateToProps = (state) => {
    return {
        activeProfile: state.entities.activeProfile,
        profileQuery: state.queries[queryKey],
        originalUser: state.entities.originalUser,
        hotelBrandSelection: state.hotelBrandSelection,
    };
};
 
class FrameMain extends React.Component<IProps, IState> {
    static displayName: string = 'FrameMain';
 
    constructor(props: IProps) {
        super(props);
        this.state = {
            requiredStepsShown: false,
            brandId: 0,
            hotelId: 0,
            isLqaAcademyModalOpen: shouldShowLqaAcademyModal(),
        };
        // adding a custom event to update the brandId
        document.addEventListener('updateBrandHotelId', (evnt: any) => {
            const brandId = evnt.brandId;
            const hotelId = evnt.hotelId;
            this.setState({ brandId, hotelId});
            setItem('cachedBrandSelected', brandId);
            setItem('cachedHotelSelected', hotelId);
        });
    }
 
    handleOverlayClose = () => {
        this.props.closeOverlay('all');
        this.props.history.replace({ hash: '' });
    };
 
    handleNetworkStatus = (e) => {
        if (e.type === 'online') {
            this.props.closeOverlay('offline');
        }
        if (e.type === 'offline') {
            this.props.openOverlay('offline', true);
        }
    };
 
    componentDidMount() {
        window.addEventListener('online', this.handleNetworkStatus);
        window.addEventListener('offline', this.handleNetworkStatus);
 
        const header = getHeaders();
        const pathname = window.location.href.split('/').reverse()[0];
        if (
            !!header.Authorization &&
            pathname !== 'logout'
        ) {
            fetch(`${Env.API_URL}/auth/me`, {
                method: 'GET',
                headers: getHeaders(),
            }).then(response => {
                if (response.status === 401) {
                    window.location.href = '/logout';
                    setItem('logout', 'yes');
                    return;
                } else if (response.status === 200) {
                    setItem('logout', 'no');
                }
                return response.json();
            }).then(res => {
                const newToken = res.accessToken;
                const token = getItem('user');
                token.token = newToken;
                token.user = res.user;
                setItem('user', token);
            });
        }
    }
 
    componentWillUnmount() {
        window.removeEventListener('online', this.handleNetworkStatus);
        window.removeEventListener('offline', this.handleNetworkStatus);
    }
 
    render() {
        if (this.props.location.pathname === '/sso/login') {
            return (
                <LoginSSO />
            );
        }
        if (this.props.location.pathname === '/sso/saml/login') {
            return (
                <LoginSAML />
            );
        }
        const splitUrl = split(this.props.location.pathname, this.props.match.url);
        let urlSegment = '';
        if (splitUrl.length > 1) {
            urlSegment = splitUrl[1];
        }
        while (urlSegment.charAt(0) === '/') {
            urlSegment = urlSegment.substr(1);
        }
        if (urlSegment === 'emailverification') {
            return (
                <EmailVerification
                    token={splitUrl[2]}
                    queries=""
                    history={this.props.history}
                    location={this.props.location}
                    match={this.props.match}
                />
            );
        }
 
        if (!this.props.activeProfile) {
            return <ScreenLogin />;
        }
 
        const onAdminSide = isOnAdminSide(this.props.location);
        const userData = getUserData(onAdminSide);
        const accountType = getUserAccountType(userData);
        const userManagementAllowed = isUserManagementAllowed(userData);
        const isViewer = isUserViewer(userData);
        const isTrainer = userData.isTrainer ? userData.isTrainer : false;
        const isDepartmentManager = isUserDepartmentManager(userData);
 
        const { menuItems, userMenuItems } = getMenuItems(
            accountType,
            this.state.brandId,
            userManagementAllowed,
            isUserBlackoutEditEnabled(userData),
            this.props.match.url,
            this.props.location.pathname,
            isViewer,
            isDepartmentManager,
            isTrainer,
            isMylqaSubscriptionActive(userData),
            this.props.originalUser,
        );
 
        const userHubProps: IUserhubProps = {
            user: {
                name: `${this.props.activeProfile.userFirstName} ${this.props.activeProfile.userLastName}`,
                avatar: null,
                company: this.props.activeProfile.clientName,
            },
            representative: null,
            isOpen: this.props.overlays.indexOf('usermenu') !== -1,
            handleOpen: () => {
                this.props.closeOverlay('mainmenu');
                this.props.openOverlay('usermenu', true);
            },
            handleClose: this.handleOverlayClose,
            menuitems: userMenuItems,
        };
 
        if (this.props.originalUser) {
            userHubProps.representative = {
                name: `${this.props.activeProfile.userFirstName} ${this.props.activeProfile.userLastName}`,
                avatar: null,
                company: this.props.activeProfile.clientName,
                role: this.props.activeProfile.userRole,
                onDiscard: () => {
                    const originalUser = getItem('originalUser');
                    removeItem('originalUser');
                    removeItem('selectedDate');
                    removeItem('lqaUser');
                    setItem('user', JSON.stringify(originalUser));
                    this.props.restoreActiveProfile(originalUser.user);
                    this.props.history.push('/admin/clients/users');
                },
            };
            userHubProps.user = {
                name: `${this.props.originalUser.userFirstName} ${this.props.originalUser.userLastName}`,
                avatar: null,
                company: this.props.originalUser.clientName,
            };
        }
 
        return (
          <>
            <FrameAdmin
              menuitems={menuItems}
              title="LQA"
              locale={this.props.match.params.locale}
              location={this.props.location}
              userhubProps={userHubProps}
              logoHref={generateLocaledUrl(this.props.match.url, '')}
              onAdmin={!!onAdminSide}
            >
              {!onAdminSide && <TutorialVideoButton />}
                    {this.props.profileQuery && !this.props.profileQuery.isPending && (
                        this.props.children
                    )}
              <MenuMobile
                isOpen={this.props.overlays.indexOf('mobilemenu') !== -1}
                doClose={this.handleOverlayClose}
                menuitems={menuItems}
              />
              <ScrollLockingOverlay
                isOpen={this.props.overlays.indexOf('offline') !== -1}
                doClose={this.handleOverlayClose}
                layout="focusview"
              >
                        <ErrorOffline />
              </ScrollLockingOverlay>
              {!this.props.profileQuery ||
                (this.props.profileQuery.isPending && <LoadingContent />)}
              <Modal
                title={''}
                isOpen={this.state.isLqaAcademyModalOpen}
                setIsOpen={(close) => this.setState({ isLqaAcademyModalOpen: close })}
                size="max"
                outSideClickClose={true}
              >
                <LqaAcademyNews onClose={() => this.setState({ isLqaAcademyModalOpen: false })} />
              </Modal>
            </FrameAdmin>
          </>
        );
    }
}
 
const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): {} => ({
    logout: () => {
        return dispatch(mutateAsync(logoutRequest({ body: {}, type: 'logout', queryKey: 'logout' })));
    },
    restoreActiveProfile: (profile: IProfile) => {
        dispatch(updateEntities<any>({
            activeProfile: () => profile,
            originalUser: () => undefined,
        }));
    },
});
 
export default compose<any>(
    withRouter,
    connect(mapStateToProps, mapDispatchToProps),
    connectRequest((props) => activeProfileRequest({ body: props.filter, type: 'activeProfile', queryKey })),
    withTranslation(),
    withOverlays
)(FrameMain);
